import { groupBySection, packColumns, type MenuBlock, type MenuColumn } from '../paginate.js';
import type { MenuBoardContent } from '../types.js';
import { image, type MenuTemplate, type RenderContext } from './common.js';
import {
  calloutIds,
  heroPhotoId,
  spBrand,
  spCallout,
  spCalloutHeight,
  spCss,
  spFamily,
  spHead,
  spHeadHeight,
  spItemHeight,
  spLine,
  spSlogan,
  SP_KINDS,
  SP_THEME,
  SP_VARIANTS,
  type SpMetrics,
} from './stack-panels-five.js';

/**
 * Stack Panels, bands: the same fills and type as horizontal bands. A black hero band with the
 * brand, slogan and photo runs across the top, then one band per section in cycling colours,
 * its heading and call-out on the left (on top in portrait), its items in three columns (two in
 * portrait) and its section photo bleeding off the end. Made for portrait screens; works in
 * landscape too.
 */

const ID = 'stack-panels-bands';
const S = `.mb-t-${ID}`;
const P = 'spb';

function band(ctx: RenderContext, b: MenuBlock, i: number, callouts: Set<string>): string {
  const callout = b.items.find((item) => callouts.has(item.id));
  const items = b.items
    .filter((item) => item !== callout)
    .map((item) => spLine(ctx, item, P))
    .join('');
  const photo = b.continued ? '' : image(ctx, b.section.imageMediaId, `${P}-ph-img`);
  const head = spHead(ctx, b, P, 11);
  return [
    `<div class="${P}-band ${P}-k-${SP_KINDS[i % SP_KINDS.length]}${photo ? ` ${P}-has-ph` : ''}${head || callout ? '' : ` ${P}-nohead`}">`,
    head || callout ? `<div class="${P}-headcell">${head}${callout ? spCallout(ctx, callout, P) : ''}</div>` : '',
    `<div class="${P}-items">${items}</div>`,
    photo ? `<div class="${P}-ph">${photo}</div>` : '',
    '</div>',
  ].join('');
}

function column(board: MenuBoardContent, portrait: boolean): MenuColumn {
  const callouts = calloutIds(board);
  const cols = portrait ? 2 : 3;
  const m: SpMetrics = {
    head: 5.2,
    nameChars: portrait ? 17 : 18,
    item: { nameEm: 1.45, descEm: 0.85, perLine: portrait ? 36 : 40, gap: 1.15 },
  };
  return {
    capacity: portrait ? 92 : 51,
    blockCost: (section, items, continued) => {
      const callout = items.find((item) => callouts.has(item.id));
      const rest = items.filter((item) => item !== callout);
      let body = 0;
      for (let r = 0; r < rest.length; r += cols) {
        body += Math.max(...rest.slice(r, r + cols).map((item) => spItemHeight(board, item, m)));
      }
      const head = spHeadHeight(board, section, continued, m.head);
      const call = callout ? spCalloutHeight(board, callout, portrait ? 40 : 28) : 0;
      const photo = !continued && section.imageMediaId ? 9 : 0;
      return portrait
        ? Math.max(head, call) + Math.max(body, photo) + 4.6
        : Math.max(head + call, body, photo) + 4.2;
    },
  };
}

export const stackPanelsBands: MenuTemplate = {
  id: ID,
  name: 'Stack panels: bands',
  description:
    'Horizontal bands in black, orange and cream: brand, slogan and photo across the top, then one band per section with its items in columns. Made for portrait screens.',
  defaultTheme: SP_THEME,
  variants: SP_VARIANTS,
  cuisines: ['burgers', 'drinks'],
  family: spFamily('Bands'),
  uses: { featured: false, sectionImages: true, brand: true, decorations: ['Hero photo in the top band'] },

  paginate(board, orientation) {
    return packColumns(groupBySection(board), [column(board, orientation === 'portrait')]);
  },

  render(ctx) {
    const callouts = calloutIds(ctx.board);
    const blocks = ctx.page.columns.flat();
    const photo = image(ctx, heroPhotoId(ctx.board), `${P}-hero-img`);
    return [
      `<div class="${P}-wrap">`,
      `<div class="${P}-hero ${P}-k-black">`,
      spBrand(ctx, P),
      spSlogan(ctx, P, 30),
      photo ? `<div class="${P}-hero-ph">${photo}</div>` : '',
      '</div>',
      `<div class="${P}-bands">`,
      blocks.length ? blocks.map((b, i) => band(ctx, b, i, callouts)).join('') : `<div class="${P}-band ${P}-k-dark"><div class="mb-empty">—</div></div>`,
      '</div>',
      '</div>',
    ].join('');
  },

  css: `
${spCss(S, P)}
${S} .${P}-wrap{flex:1;min-height:0;display:flex;flex-direction:column}
${S} .${P}-hero{position:relative;flex:0 0 15em;display:flex;align-items:center;gap:3.5em;padding:0 3em;overflow:hidden}
${S} .${P}-hero .${P}-brandblock{flex:none;max-width:30em}
${S} .${P}-sloganbox{position:relative;z-index:1;flex:0 1 auto;min-width:0;max-width:34em}
${S} .${P}-hero-ph{position:absolute;top:0;bottom:0;inset-inline-end:0;width:42%}
${S} .${P}-hero-img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;
  -webkit-mask-image:linear-gradient(to right,transparent,#000 40%);mask-image:linear-gradient(to right,transparent,#000 40%)}
${S}[dir=rtl] .${P}-hero-img{-webkit-mask-image:linear-gradient(to left,transparent,#000 40%);mask-image:linear-gradient(to left,transparent,#000 40%)}
${S}.mb-portrait .${P}-hero{flex-basis:26em;flex-direction:column;align-items:flex-start;justify-content:center;gap:2.2em}
${S}.mb-portrait .${P}-hero-ph{width:55%}
${S}.mb-portrait .${P}-sloganbox{max-width:24em}
${S} .${P}-bands{flex:1;min-height:0;display:flex;flex-direction:column}
${S} .${P}-band{--${P}-phw:0em;position:relative;flex:1 1 auto;min-height:0;overflow:hidden;display:grid;align-items:center;align-content:center;
  grid-template-columns:22em 1fr;gap:0 3em;padding:2em 3em;padding-inline-end:calc(3em + var(--${P}-phw))}
${S} .${P}-band.${P}-has-ph{--${P}-phw:19em}
${S} .${P}-headcell{min-width:0}
${S} .${P}-band.${P}-nohead{grid-template-columns:1fr}
${S} .${P}-head{margin-bottom:0}
${S} .${P}-head+.${P}-call{margin-top:1.6em}
${S} .${P}-items{min-width:0;display:grid;grid-template-columns:repeat(3,1fr);gap:0 2.5em;align-content:center}
${S} .${P}-item{margin:.55em 0}
${S} .${P}-ph{position:absolute;top:0;bottom:0;inset-inline-end:0;width:calc(var(--${P}-phw) - 2em)}
${S} .${P}-ph-img{-webkit-mask-image:linear-gradient(to right,transparent,#000 35%);mask-image:linear-gradient(to right,transparent,#000 35%)}
${S}[dir=rtl] .${P}-ph-img{-webkit-mask-image:linear-gradient(to left,transparent,#000 35%);mask-image:linear-gradient(to left,transparent,#000 35%)}
${S} .${P}-k-hot .${P}-ph-img,${S} .${P}-k-cream .${P}-ph-img{-webkit-mask-image:none;mask-image:none}
${S}.mb-portrait .${P}-band{grid-template-columns:1fr;padding:2.2em 2.6em;padding-inline-end:calc(2.6em + var(--${P}-phw));gap:1.6em 0}
${S}.mb-portrait .${P}-band.${P}-has-ph{--${P}-phw:17em}
${S}.mb-portrait .${P}-headcell{display:flex;align-items:flex-end;justify-content:space-between;gap:2em}
${S}.mb-portrait .${P}-head+.${P}-call{margin-top:0}
${S}.mb-portrait .${P}-call{flex:0 1 24em}
${S}.mb-portrait .${P}-items{grid-template-columns:repeat(2,1fr)}
`,
};
